import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { IconPicker } from '~/components/chat/commands/IconPicker';
import { KeyboardShortcut } from '~/components/chat/commands/KeyboardShortcut';
import * as PopupForm from '~/components/chat/commands/PopupForm';
import View from '~/components/chat/commands/View';
import { PromptCategoryCreate } from '~/components/chat/commands/views/PromptCategoryCreate';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { commandStore } from '~/lib/stores/command';
import type { PromptCommandCategory } from '~/lib/stores/command/models/PromptCommandCategory';
import { ViewCommand } from '~/lib/stores/command/models/ViewCommand';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { validateFormData } from '~/utils/form';

export const PromptCreate = ({
  onCancel,
  onCreate,
  categories,
  preserveFormValues,
  defaultValues,
}: {
  onCancel: () => void;
  onCreate: (data: { name: string; prompt: string; category?: string; icon?: string }) => void;
  categories: PromptCommandCategory[];
  preserveFormValues: (values: any) => void;
  defaultValues?: Record<string, string>;
}) => {
  const schema = z.object({
    name: z.string().min(1, 'Name is required').max(100, 'Name must be at most 100 characters long'),
    prompt: z.string().min(1, 'Prompt is required'),
    category: z.string().optional(),
    icon: z.string().optional(),
  });

  const initialInputRef = useRef<HTMLButtonElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [errors, setErrors] = useState<Partial<z.infer<typeof schema>>>({});

  const [category, setCategory] = useState<string | undefined>(defaultValues?.category);
  const [icon, setIcon] = useState<string>(defaultValues?.icon || '');

  useEffect(() => {
    if (initialInputRef.current) {
      requestAnimationFrame(() => initialInputRef.current?.focus());
    }
  }, []);

  function handleSubmit() {
    const formData = new FormData(formRef.current!);

    formData.set('icon', icon);

    validateFormData(formData, schema, {
      onSuccess: (data) => onCreate(data),
      onError: (errors) => setErrors(errors),
    });
  }

  function handleCreateCategory() {
    const formData = new FormData(formRef.current!);
    const values = [...formData.entries()];

    preserveFormValues(values);

    commandStore.router.push(
      new ViewCommand({
        id: 'prompt-create-category',
        name: 'Create Category',
        icon: 'i-lucide:plus',
        view: () => (
          <PromptCategoryCreate
            onCancel={() => commandStore.router.pop()}
            onCreate={(data) => {
              const newCategory = commandStore.addCustomPromptCategory(data);

              // prettier-ignore
              preserveFormValues([
                ...values,
                ['category', newCategory.id],
                ['icon', newCategory.icon]
              ]);

              sendAnalyticsEvent({
                action: AnalyticsAction.Track,
                payload: {
                  event: AnalyticsTrackEvent.PromptLibraryCategoryCreated,
                  properties: {
                    categoryId: newCategory.id,
                    categoryName: newCategory.name,
                  },
                },
              });

              setIcon(newCategory.icon || '');

              commandStore.router.pop();
            }}
          />
        ),
      }),
    );
  }

  return (
    <View.Container>
      <View.Header title="Create Prompt" description="Add a prompt to your personal prompt library" />

      <View.Content>
        <PopupForm.Root ref={formRef}>
          <PopupForm.Select
            ref={initialInputRef}
            name="category"
            label="Category"
            placeholder="Select a category"
            value={category}
            options={[
              ...categories.map((category) => ({
                value: category.id,
                label: category.name,
                icon: category.icon,
              })),
              { type: 'separator', id: 'separator-1' },
              {
                type: 'action',
                label: 'Create new category',
                icon: 'i-lucide:plus',
                onClick: handleCreateCategory,
              },
            ]}
            error={errors.category}
            onValueChange={(value) => {
              setErrors(({ category, ...rest }) => rest);
              setCategory(value);

              const categoryIcon = categories.find((category) => category.id === value)?.icon;
              setIcon(categoryIcon || '');
            }}
          />

          <div className="flex gap-2">
            <IconPicker name="icon" label="Icon" value={icon} onValueChange={setIcon} />
            <PopupForm.Input
              label="Name"
              size="sm"
              name="name"
              defaultValue={defaultValues?.name}
              placeholder="Name your prompt..."
              className="w-full text-sm"
              error={errors.name}
              onChange={() => setErrors(({ name, ...rest }) => rest)}
            />
          </div>

          <PopupForm.Textarea
            label="Prompt"
            name="prompt"
            defaultValue={defaultValues?.prompt}
            placeholder="Write your prompt..."
            error={errors.prompt}
            onChange={() => setErrors(({ prompt, ...rest }) => rest)}
          />
        </PopupForm.Root>
      </View.Content>

      <View.Footer>
        <KeyboardShortcut label="Cancel" keys={['Escape']} action={onCancel} />
        <KeyboardShortcut label="Create Prompt" keys={['Meta', 'Enter']} action={handleSubmit} />
      </View.Footer>
    </View.Container>
  );
};
